import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from 'react-toastify';
import {
  obtenerVeterinarios,
  getDisponibilidadVet,
  getCitasDeVet,
  crearCitaDeVet,
  cancelarCitaDeVet,
  obtenerMisVeterinarios,
  obtenerVeterinariosDeUsuario,
  obtenerCitasPendientes,
  confirmarCitaPropuesta,
  rechazarCitaPropuesta,
  obtenerRecordatoriosCitas,
  crearRecordatorioCita,
  eliminarRecordatorioCita,
  invitarVeterinarioAUsuario,
} from "../../services/api";
import AgregarVeterinarioModal from "./AgregarVeterinarioModal";

// ─────────────────────────────────────────────────────────────
// Paleta de colores clara (consistente con panel de usuario)
// ─────────────────────────────────────────────────────────────
const COLORS = {
  bgMain: '#f5f5f5',
  bgCard: '#ffffff',
  bgSecondary: '#f8f9fa',
  textPrimary: '#1a1a1a',
  textSecondary: '#6c757d',
  textMuted: '#9ca3af',
  accent: '#6ab04c',
  accentDark: '#5a9a3c',
  accentGlow: 'rgba(106, 176, 76, 0.1)',
  border: 'rgba(0, 0, 0, 0.1)',
  borderLight: 'rgba(0, 0, 0, 0.05)',
  danger: '#ef4444',
  warning: '#f59e0b',
  success: '#22c55e',
};

/**
 * UserCalendarCitas.jsx - Flujo tipo Calendly
 *
 * SECCIÓN 1: Mis Citas (ver y cancelar citas existentes)
 * SECCIÓN 2: Agendar Nueva Cita
 *   - Paso 1: Seleccionar fecha (calendario simple)
 *   - Paso 2: Seleccionar horario (botones)
 *   - Paso 3: Confirmar cita (duración + motivo)
 */

// ─────────────────────────────────────────────────────────────
// Componente: Indicador de Progreso
// ─────────────────────────────────────────────────────────────
function ProgressIndicator({ step }) {
  const labels = ["Fecha", "Horario", "Confirmar"];

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-center">
        {[1, 2, 3].map((s, i) => (
          <React.Fragment key={s}>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: 32,
                height: 32,
                fontSize: '0.85rem',
                fontWeight: 'bold',
                backgroundColor: step >= s ? COLORS.accent : COLORS.bgCard,
                color: step >= s ? '#fff' : COLORS.textMuted,
                border: step >= s ? 'none' : `1px solid ${COLORS.border}`
              }}
            >
              {step > s ? '✓' : s}
            </div>
            {i < 2 && (
              <div
                className="mx-2"
                style={{
                  width: 50,
                  height: 3,
                  borderRadius: 2,
                  backgroundColor: step > s ? COLORS.accent : COLORS.bgCard
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="text-center mt-2" style={{ color: COLORS.textSecondary }}>
        <small>Paso {step} de 3: <strong style={{ color: COLORS.textPrimary }}>{labels[step - 1]}</strong></small>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente: Sección Mis Citas
// ─────────────────────────────────────────────────────────────
function MisCitasSection({ citas, veterinarios, onCancelar, loading }) {
  const formatearFecha = (fechaStr) => {
    const fecha = new Date(fechaStr + 'T00:00:00');
    return fecha.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatearHora = (hora) => {
    const [h, m] = hora.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  const getVetNombre = (cita) => {
    // Primero intentar usar vetNombre almacenado en la cita
    if (cita.vetNombre) return cita.vetNombre;
    // Fallback: buscar en la lista de veterinarios
    const vet = veterinarios.find(v => v.id === cita.vetId);
    return vet?.nombre || 'Veterinario';
  };

  // Verificar si una cita ya paso
  const esCitaPasada = (cita) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaCita = new Date(cita.fecha + 'T23:59:59');
    return fechaCita < hoy;
  };

  if (loading) {
    return (
      <div className="card mb-4" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
        <div className="card-body text-center py-4">
          <div className="spinner-border spinner-border-sm me-2" style={{ color: COLORS.accent }}></div>
          <span style={{ color: COLORS.textSecondary }}>Cargando tus citas...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
      <div className="card-header" style={{ backgroundColor: COLORS.bgSecondary, borderBottom: `1px solid ${COLORS.border}` }}>
        <h5 className="mb-0" style={{ color: COLORS.textPrimary }}>
          <i className="bi bi-check-circle-fill me-2" style={{ color: COLORS.accent }}></i>
          Citas con Veterinario
        </h5>
      </div>
      <div className="card-body">
        {citas.length === 0 ? (
          <div className="text-center py-4" style={{ color: COLORS.textMuted }}>
            <i className="bi bi-calendar-x" style={{ fontSize: '2rem' }}></i>
            <p className="mt-2 mb-0">No tienes citas agendadas</p>
          </div>
        ) : (
          <div className="list-group list-group-flush">
            {citas.map((cita) => (
              <div
                key={cita.id}
                className="px-0 py-3"
                style={{ borderBottom: `1px solid ${COLORS.borderLight}`, backgroundColor: 'transparent' }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <span
                        className="badge me-2"
                        style={{
                          backgroundColor: cita.estado === 'confirmada' ? COLORS.success : COLORS.warning,
                          color: cita.estado === 'confirmada' ? '#fff' : '#000'
                        }}
                      >
                        {cita.estado === 'confirmada' ? '✓ Confirmada' : '⏳ Pendiente'}
                      </span>
                      {esCitaPasada(cita) && (
                        <span
                          className="badge"
                          style={{ backgroundColor: COLORS.textMuted, color: '#fff' }}
                        >
                          Pasada
                        </span>
                      )}
                    </div>
                    <div className="mb-1" style={{ color: COLORS.textPrimary }}>
                      <i className="bi bi-calendar3 me-2" style={{ color: COLORS.textMuted }}></i>
                      <strong>{formatearFecha(cita.fecha)}</strong>
                      <span className="mx-2" style={{ color: COLORS.textMuted }}>•</span>
                      <i className="bi bi-clock me-1" style={{ color: COLORS.textMuted }}></i>
                      {formatearHora(cita.horaInicio)}
                      {cita.horaFin && ` - ${formatearHora(cita.horaFin)}`}
                    </div>
                    <div className="small mb-1" style={{ color: COLORS.textSecondary }}>
                      <i className="bi bi-person-badge me-1"></i>
                      {getVetNombre(cita)}
                    </div>
                    {cita.motivo && (
                      <div className="small" style={{ color: COLORS.textSecondary }}>
                        <i className="bi bi-chat-left-text me-1"></i>
                        {cita.motivo}
                      </div>
                    )}
                  </div>
                  {/* Solo mostrar boton cancelar si la cita no ha pasado */}
                  {!esCitaPasada(cita) && (
                    <button
                      className="btn btn-sm"
                      style={{ color: COLORS.danger, border: `1px solid ${COLORS.danger}`, backgroundColor: 'transparent' }}
                      onClick={() => onCancelar(cita)}
                      title="Cancelar cita"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente: Citas Propuestas por Veterinarios
// ─────────────────────────────────────────────────────────────
function CitasPropuestasSection({
  citas,
  loading,
  processingCita,
  onConfirmar,
  onRechazar
}) {
  const formatearFecha = (fechaStr) => {
    const fecha = new Date(fechaStr + 'T00:00:00');
    return fecha.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatearHora = (hora) => {
    const [h, m] = hora.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  if (loading) {
    return null; // No mostrar mientras carga
  }

  if (citas.length === 0) {
    return null; // No mostrar si no hay citas propuestas
  }

  return (
    <div className="card mb-4" style={{
      backgroundColor: '#fffbeb',
      border: `2px solid ${COLORS.warning}`
    }}>
      <div className="card-header" style={{
        backgroundColor: COLORS.warning,
        borderBottom: 'none'
      }}>
        <h5 className="mb-0" style={{ color: '#fff' }}>
          <i className="bi bi-bell-fill me-2"></i>
          Citas Propuestas por tu Veterinario
        </h5>
      </div>
      <div className="card-body">
        <p style={{ color: COLORS.textSecondary, marginBottom: '16px' }}>
          Tu veterinario te ha propuesto las siguientes citas. Confirma o rechaza para continuar.
        </p>
        <div className="list-group list-group-flush">
          {citas.map((cita) => (
            <div
              key={cita.id}
              className="px-0 py-3"
              style={{ borderBottom: `1px solid ${COLORS.borderLight}`, backgroundColor: 'transparent' }}
            >
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-2">
                    <span
                      className="badge me-2"
                      style={{ backgroundColor: COLORS.warning, color: '#000' }}
                    >
                      Pendiente de confirmar
                    </span>
                    {cita.tipoConsulta && (
                      <span
                        className="badge"
                        style={{ backgroundColor: COLORS.accent, color: '#fff' }}
                      >
                        {cita.tipoConsulta}
                      </span>
                    )}
                  </div>
                  <div className="mb-1" style={{ color: COLORS.textPrimary }}>
                    <i className="bi bi-calendar3 me-2" style={{ color: COLORS.textMuted }}></i>
                    <strong>{formatearFecha(cita.fecha)}</strong>
                    <span className="mx-2" style={{ color: COLORS.textMuted }}>•</span>
                    <i className="bi bi-clock me-1" style={{ color: COLORS.textMuted }}></i>
                    {formatearHora(cita.horaInicio)}
                  </div>
                  <div className="small mb-1" style={{ color: COLORS.textSecondary }}>
                    <i className="bi bi-person-badge me-1"></i>
                    {cita.veterinario?.nombre || 'Veterinario'}
                  </div>
                  {cita.objetoName && (
                    <div className="small mb-1" style={{ color: COLORS.textSecondary }}>
                      <i className="bi bi-heart me-1"></i>
                      {cita.objetoName}
                    </div>
                  )}
                  {cita.motivo && (
                    <div className="small" style={{ color: COLORS.textSecondary }}>
                      <i className="bi bi-chat-left-text me-1"></i>
                      {cita.motivo}
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: COLORS.success,
                      color: '#fff',
                      border: 'none'
                    }}
                    onClick={() => onConfirmar(cita)}
                    disabled={processingCita === cita.id}
                  >
                    {processingCita === cita.id ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-1"></i>
                        Confirmar
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: 'transparent',
                      color: COLORS.danger,
                      border: `1px solid ${COLORS.danger}`
                    }}
                    onClick={() => onRechazar(cita)}
                    disabled={processingCita === cita.id}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    Rechazar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente: Selector de Veterinario (Onboarding + Compact)
// ─────────────────────────────────────────────────────────────
function VetSelector({ veterinarios, onSelect, onBuscarVet, onCrearRecordatorio, onAgregarVet, isOnboarding }) {
  const makeHover = (color) => ({
    onMouseEnter: (e) => {
      e.currentTarget.style.borderColor = color;
      e.currentTarget.style.boxShadow = `0 0 12px ${color}20`;
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.borderColor = COLORS.border;
      e.currentTarget.style.boxShadow = 'none';
    }
  });

  const cardBase = {
    cursor: 'pointer',
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.bgSecondary,
    transition: 'all 0.2s'
  };

  // ── Onboarding: 3 large option cards ──
  if (isOnboarding) {
    return (
      <div className="py-3">
        <div className="text-center mb-4">
          <i className="bi bi-calendar-heart" style={{ fontSize: '2.5rem', color: COLORS.accent }}></i>
          <h5 className="mt-2 mb-1" style={{ color: COLORS.textPrimary }}>
            ¡Empieza a gestionar las citas!
          </h5>
          <p className="mb-0" style={{ color: COLORS.textSecondary, fontSize: '0.9rem' }}>
            Elige cómo quieres comenzar
          </p>
        </div>

        {/* Agregar veterinario */}
        <div className="card mb-3" style={cardBase} onClick={onAgregarVet} {...makeHover(COLORS.accent)}>
          <div className="card-body d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: 48, height: 48, backgroundColor: COLORS.accentGlow, flexShrink: 0 }}>
              <i className="bi bi-person-plus-fill" style={{ fontSize: '1.25rem', color: COLORS.accent }}></i>
            </div>
            <div className="flex-grow-1">
              <h6 className="mb-1" style={{ color: COLORS.textPrimary }}>Agrega tu veterinario</h6>
              <small style={{ color: COLORS.textSecondary }}>Invita a tu vet a conectarse contigo</small>
            </div>
            <i className="bi bi-chevron-right" style={{ color: COLORS.textMuted }}></i>
          </div>
        </div>

        {/* Buscar en directorio */}
        <div className="card mb-3" style={cardBase} onClick={onBuscarVet} {...makeHover('#3b82f6')}>
          <div className="card-body d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: 48, height: 48, backgroundColor: 'rgba(59, 130, 246, 0.1)', flexShrink: 0 }}>
              <i className="bi bi-search" style={{ fontSize: '1.25rem', color: '#3b82f6' }}></i>
            </div>
            <div className="flex-grow-1">
              <h6 className="mb-1" style={{ color: COLORS.textPrimary }}>Buscar en directorio</h6>
              <small style={{ color: COLORS.textSecondary }}>Encuentra una veterinaria cerca de ti</small>
            </div>
            <i className="bi bi-chevron-right" style={{ color: COLORS.textMuted }}></i>
          </div>
        </div>

        {/* Crear cita personal */}
        <div className="card" style={cardBase} onClick={onCrearRecordatorio} {...makeHover(COLORS.warning)}>
          <div className="card-body d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: 48, height: 48, backgroundColor: 'rgba(245, 158, 11, 0.1)', flexShrink: 0 }}>
              <i className="bi bi-pencil-square" style={{ fontSize: '1.25rem', color: COLORS.warning }}></i>
            </div>
            <div className="flex-grow-1">
              <h6 className="mb-1" style={{ color: COLORS.textPrimary }}>Crear cita personal</h6>
              <small style={{ color: COLORS.textSecondary }}>Anota tu próxima cita para no olvidarla</small>
            </div>
            <i className="bi bi-chevron-right" style={{ color: COLORS.textMuted }}></i>
          </div>
        </div>
      </div>
    );
  }

  // ── Returning user: vet list + compact action buttons ──
  return (
    <div>
      {veterinarios.length > 0 && (
        <div className="mb-3">
          <p className="small mb-2" style={{ color: COLORS.textSecondary, fontWeight: 500 }}>
            Agendar con tu veterinario:
          </p>
          <div className="row g-2">
            {veterinarios.map((vet) => (
              <div key={vet.id} className="col-12">
                <div
                  className="card"
                  style={{ ...cardBase, border: `1px solid ${COLORS.border}` }}
                  onClick={() => onSelect(vet.id)}
                  {...makeHover(COLORS.accent)}
                >
                  <div className="card-body py-2 px-3 d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                      style={{ width: 36, height: 36, backgroundColor: COLORS.accentGlow }}>
                      <i className="bi bi-person-badge" style={{ fontSize: '1rem', color: COLORS.accent }}></i>
                    </div>
                    <div className="flex-grow-1">
                      <span style={{ color: COLORS.textPrimary, fontWeight: 500, fontSize: '0.9rem' }}>{vet.nombre}</span>
                      {vet.especialidad && <small className="d-block" style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>{vet.especialidad}</small>}
                    </div>
                    <i className="bi bi-chevron-right" style={{ color: COLORS.textMuted, fontSize: '0.8rem' }}></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="d-flex flex-wrap gap-2">
        <button
          className="btn btn-sm flex-fill"
          style={{ backgroundColor: 'transparent', color: COLORS.accent, border: `1px solid ${COLORS.accent}`, fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
          onClick={onAgregarVet}
        >
          <i className="bi bi-person-plus me-1"></i>Agregar vet
        </button>
        <button
          className="btn btn-sm flex-fill"
          style={{ backgroundColor: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6', fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
          onClick={onBuscarVet}
        >
          <i className="bi bi-search me-1"></i>Buscar directorio
        </button>
        <button
          className="btn btn-sm flex-fill"
          style={{ backgroundColor: 'transparent', color: COLORS.warning, border: `1px solid ${COLORS.warning}`, fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
          onClick={onCrearRecordatorio}
        >
          <i className="bi bi-pencil-square me-1"></i>Recordatorio
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente: Formulario de Recordatorio Personal
// ─────────────────────────────────────────────────────────────
function RecordatorioForm({ objetoName, onSubmit, onCancel, isLoading, lugarPrellenado }) {
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [lugar, setLugar] = useState(lugarPrellenado || '');
  const [motivo, setMotivo] = useState('');
  const [notas, setNotas] = useState('');

  const hoy = new Date().toISOString().slice(0, 10);

  const motivosOpciones = ['Vacunas', 'Consulta', 'Desparasitación', 'Cirugía', 'Revisión general', 'Otro'];

  const handleSubmit = () => {
    if (!fecha || !hora) return;
    onSubmit({ fecha, hora, lugar, motivo, notas });
  };

  return (
    <div className="card" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
      <div className="card-body">
        <h5 className="card-title mb-4 text-center" style={{ color: COLORS.textPrimary }}>
          <i className="bi bi-pencil-square me-2" style={{ color: COLORS.warning }}></i>
          Nuevo Recordatorio
        </h5>

        {objetoName && (
          <div className="rounded p-2 mb-3 text-center" style={{ backgroundColor: COLORS.bgSecondary }}>
            <small style={{ color: COLORS.textSecondary }}>Para: </small>
            <strong style={{ color: COLORS.textPrimary }}>{decodeURIComponent(objetoName)}</strong>
          </div>
        )}

        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary }}>
            <i className="bi bi-calendar3 me-1"></i> Fecha <span style={{ color: COLORS.danger }}>*</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={fecha}
            min={hoy}
            onChange={(e) => setFecha(e.target.value)}
            style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary }}>
            <i className="bi bi-clock me-1"></i> Hora <span style={{ color: COLORS.danger }}>*</span>
          </label>
          <input
            type="time"
            className="form-control"
            value={hora}
            onChange={(e) => setHora(e.target.value)}
            style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary }}>
            <i className="bi bi-geo-alt me-1"></i> Lugar
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Nombre de la veterinaria o dirección"
            value={lugar}
            onChange={(e) => setLugar(e.target.value)}
            maxLength={200}
            style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary }}>
            <i className="bi bi-tag me-1"></i> Motivo
          </label>
          <div className="d-flex flex-wrap gap-2">
            {motivosOpciones.map(op => (
              <button
                key={op}
                type="button"
                className="btn btn-sm"
                style={{
                  backgroundColor: motivo === op ? COLORS.accent : 'transparent',
                  color: motivo === op ? '#fff' : COLORS.textSecondary,
                  border: `1px solid ${motivo === op ? COLORS.accent : COLORS.border}`,
                }}
                onClick={() => setMotivo(motivo === op ? '' : op)}
              >
                {op}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary }}>
            <i className="bi bi-chat-left-text me-1"></i> Notas
          </label>
          <textarea
            className="form-control"
            rows={2}
            placeholder="Notas adicionales..."
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            maxLength={300}
            style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
          />
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn flex-grow-1"
            style={{ backgroundColor: 'transparent', color: COLORS.textSecondary, border: `1px solid ${COLORS.border}` }}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            className="btn flex-grow-1"
            style={{ backgroundColor: COLORS.accent, color: '#fff', border: 'none' }}
            onClick={handleSubmit}
            disabled={!fecha || !hora || isLoading}
          >
            {isLoading ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</>
            ) : (
              <><i className="bi bi-check-lg me-1"></i>Guardar Recordatorio</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente: Calendario Simple (CSS Grid)
// ─────────────────────────────────────────────────────────────
function SimpleCalendar({ availableDates, selectedDate, onSelectDate }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const diasSemana = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const diasDelMes = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const primerDia = new Date(year, month, 1);
    const ultimoDia = new Date(year, month + 1, 0);
    let diaInicio = primerDia.getDay() - 1;
    if (diaInicio < 0) diaInicio = 6;

    const dias = [];
    for (let i = 0; i < diaInicio; i++) {
      dias.push({ day: null, date: null });
    }
    for (let d = 1; d <= ultimoDia.getDate(); d++) {
      dias.push({ day: d, date: new Date(year, month, d) });
    }
    return dias;
  }, [currentMonth]);

  const tieneDisponibilidad = (fecha) => {
    if (!fecha) return false;
    return availableDates.some(d =>
      d.getFullYear() === fecha.getFullYear() &&
      d.getMonth() === fecha.getMonth() &&
      d.getDate() === fecha.getDate()
    );
  };

  const esFechaPasada = (fecha) => {
    if (!fecha) return true;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return fecha < hoy;
  };

  const esSeleccionada = (fecha) => {
    if (!fecha || !selectedDate) return false;
    return fecha.toDateString() === selectedDate.toDateString();
  };

  const esHoy = (fecha) => {
    if (!fecha) return false;
    return fecha.toDateString() === new Date().toDateString();
  };

  return (
    <div className="card" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button
            className="btn btn-sm"
            style={{ color: COLORS.textSecondary, border: `1px solid ${COLORS.borderLight}`, backgroundColor: 'transparent' }}
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          <h5 className="mb-0" style={{ color: COLORS.textPrimary }}>
            {meses[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h5>
          <button
            className="btn btn-sm"
            style={{ color: COLORS.textSecondary, border: `1px solid ${COLORS.borderLight}`, backgroundColor: 'transparent' }}
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>

        <div className="d-grid mb-2" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {diasSemana.map(dia => (
            <div key={dia} className="text-center small fw-bold py-2" style={{ color: COLORS.textSecondary }}>
              {dia}
            </div>
          ))}
        </div>

        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {diasDelMes.map((item, idx) => {
            if (!item.day) return <div key={idx} className="p-2"></div>;

            const disponible = tieneDisponibilidad(item.date);
            const pasada = esFechaPasada(item.date);
            const seleccionada = esSeleccionada(item.date);
            const hoy = esHoy(item.date);
            const habilitado = disponible && !pasada;

            return (
              <button
                key={idx}
                className="btn position-relative p-2"
                style={{
                  minHeight: '44px',
                  fontSize: '0.9rem',
                  cursor: habilitado ? 'pointer' : 'not-allowed',
                  opacity: pasada ? 0.4 : 1,
                  backgroundColor: seleccionada ? COLORS.accent : 'transparent',
                  color: seleccionada ? '#fff' : habilitado ? COLORS.accent : COLORS.textMuted,
                  border: hoy ? `2px solid ${COLORS.accent}` : habilitado ? `1px solid ${COLORS.border}` : `1px solid ${COLORS.borderLight}`
                }}
                disabled={!habilitado}
                onClick={() => habilitado && onSelectDate(item.date)}
              >
                {item.day}
                {disponible && !pasada && !seleccionada && (
                  <span
                    className="position-absolute rounded-circle"
                    style={{ width: 6, height: 6, bottom: 4, left: '50%', transform: 'translateX(-50%)', backgroundColor: COLORS.success }}
                  ></span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-3 d-flex justify-content-center gap-4 small" style={{ color: COLORS.textSecondary }}>
          <span>
            <span className="rounded-circle d-inline-block me-1" style={{ width: 8, height: 8, backgroundColor: COLORS.success }}></span>
            Disponible
          </span>
          <span>
            <span className="rounded d-inline-block me-1" style={{ width: 12, height: 12, border: `2px solid ${COLORS.accent}` }}></span>
            Hoy
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente: Selector de Horarios
// ─────────────────────────────────────────────────────────────
function TimeSlotSelector({ availableSlots, selectedTime, onSelectTime, selectedDate }) {
  const formatearFecha = (fecha) => {
    return fecha.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatearHora = (hora) => {
    const [h, m] = hora.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  if (availableSlots.length === 0) {
    return (
      <div className="card" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
        <div className="card-body text-center py-5">
          <i className="bi bi-calendar-x" style={{ fontSize: '3rem', color: COLORS.textMuted }}></i>
          <h5 className="mt-3" style={{ color: COLORS.textPrimary }}>No hay horarios disponibles</h5>
          <p style={{ color: COLORS.textSecondary }}>Selecciona otra fecha.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
      <div className="card-body">
        <div className="text-center mb-4">
          <i className="bi bi-calendar-check me-2" style={{ color: COLORS.accent }}></i>
          <span className="text-capitalize" style={{ color: COLORS.textPrimary }}>{formatearFecha(selectedDate)}</span>
        </div>
        <h6 className="mb-3" style={{ color: COLORS.textPrimary }}>Selecciona un horario:</h6>
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          {availableSlots.map((slot) => (
            <button
              key={slot}
              className="btn"
              style={{
                minWidth: '110px',
                minHeight: '48px',
                fontSize: '1rem',
                backgroundColor: selectedTime === slot ? COLORS.accent : 'transparent',
                color: selectedTime === slot ? '#fff' : COLORS.accent,
                border: `1px solid ${COLORS.accent}`
              }}
              onClick={() => onSelectTime(slot)}
            >
              {formatearHora(slot)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente: Confirmación de Cita
// ─────────────────────────────────────────────────────────────
function AppointmentConfirmation({
  selectedDate, selectedTime, objetoName, vetName, duration, setDuration,
  motivo, setMotivo, onConfirm, isLoading, maxDuration
}) {
  const formatearFecha = (fecha) => {
    return fecha.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatearHora = (hora) => {
    const [h, m] = hora.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  const calcularHoraFin = () => {
    const [h, m] = selectedTime.split(':');
    const inicio = new Date();
    inicio.setHours(parseInt(h), parseInt(m), 0, 0);
    const fin = new Date(inicio.getTime() + duration * 60 * 1000);
    return formatearHora(`${fin.getHours().toString().padStart(2, '0')}:${fin.getMinutes().toString().padStart(2, '0')}`);
  };

  const duracionesDisponibles = [
    { value: 30, label: '30 minutos' },
    { value: 60, label: '1 hora' },
    { value: 90, label: '1.5 horas' },
    { value: 120, label: '2 horas' },
  ].filter(d => d.value <= maxDuration);

  return (
    <div className="card" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
      <div className="card-body">
        <h5 className="card-title mb-4 text-center" style={{ color: COLORS.textPrimary }}>
          <i className="bi bi-check-circle me-2" style={{ color: COLORS.success }}></i>
          Confirma tu cita
        </h5>

        <div className="rounded p-3 mb-4" style={{ backgroundColor: COLORS.bgSecondary, border: `1px solid ${COLORS.borderLight}` }}>
          <div className="row g-3">
            <div className="col-12">
              <div className="d-flex align-items-center">
                <i className="bi bi-calendar3 me-3" style={{ fontSize: '1.2rem', color: COLORS.accent }}></i>
                <div>
                  <small className="d-block" style={{ color: COLORS.textMuted }}>Fecha</small>
                  <span className="text-capitalize" style={{ color: COLORS.textPrimary }}>{formatearFecha(selectedDate)}</span>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-center">
                <i className="bi bi-clock me-3" style={{ fontSize: '1.2rem', color: COLORS.accent }}></i>
                <div>
                  <small className="d-block" style={{ color: COLORS.textMuted }}>Horario</small>
                  <span style={{ color: COLORS.textPrimary }}>{formatearHora(selectedTime)} - {calcularHoraFin()}</span>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-center">
                <i className="bi bi-heart me-3" style={{ fontSize: '1.2rem', color: COLORS.danger }}></i>
                <div>
                  <small className="d-block" style={{ color: COLORS.textMuted }}>Mascota</small>
                  <span style={{ color: COLORS.textPrimary }}>{decodeURIComponent(objetoName)}</span>
                </div>
              </div>
            </div>
            {vetName && (
              <div className="col-12">
                <div className="d-flex align-items-center">
                  <i className="bi bi-person-badge me-3" style={{ fontSize: '1.2rem', color: COLORS.accent }}></i>
                  <div>
                    <small className="d-block" style={{ color: COLORS.textMuted }}>Veterinario</small>
                    <span style={{ color: COLORS.textPrimary }}>{vetName}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary }}>
            <i className="bi bi-hourglass-split me-2"></i>
            Duración de la cita
          </label>
          <select
            className="form-select form-select-lg"
            value={duration}
            onChange={(e) => setDuration(parseInt(e.target.value))}
            style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
          >
            {duracionesDisponibles.map(d => (
              <option key={d.value} value={d.value}>
                {d.label} {d.value === 60 ? '(recomendado)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold" style={{ color: COLORS.textPrimary }}>
            <i className="bi bi-chat-left-text me-2"></i>
            Motivo de la cita <span style={{ color: COLORS.danger }}>*</span>
          </label>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Ej: Vacunación anual, revisión general..."
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={500}
            style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
          />
          <small style={{ color: COLORS.textMuted }}>{motivo.length}/500</small>
        </div>

        <button
          className="btn btn-lg w-100"
          style={{ backgroundColor: COLORS.accent, color: '#fff', border: 'none' }}
          onClick={onConfirm}
          disabled={!motivo.trim() || isLoading}
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Creando cita...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-2"></i>
              Confirmar Cita
            </>
          )}
        </button>

        {!motivo.trim() && (
          <div className="small text-center mt-2" style={{ color: COLORS.danger }}>
            <i className="bi bi-exclamation-circle me-1"></i>
            Debes indicar el motivo
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente: Modal de Cancelación
// ─────────────────────────────────────────────────────────────
function CancelModal({ cita, veterinarios, onConfirm, onClose, isLoading }) {
  const formatearFecha = (fechaStr) => {
    const fecha = new Date(fechaStr + 'T00:00:00');
    return fecha.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getVetNombre = () => {
    // Primero intentar usar vetNombre almacenado en la cita
    if (cita.vetNombre) return cita.vetNombre;
    // Fallback: buscar en la lista de veterinarios
    const vet = veterinarios.find(v => v.id === cita.vetId);
    return vet?.nombre || 'Veterinario';
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
          <div className="modal-header border-0">
            <h5 className="modal-title" style={{ color: COLORS.textPrimary }}>
              <i className="bi bi-exclamation-triangle me-2" style={{ color: COLORS.warning }}></i>
              Cancelar Cita
            </h5>
            <button
              className="btn-close"
              onClick={onClose}
              disabled={isLoading}
            ></button>
          </div>
          <div className="modal-body">
            <p style={{ color: COLORS.textPrimary }}>¿Estás seguro de que deseas cancelar esta cita?</p>
            <div className="rounded p-3" style={{ backgroundColor: COLORS.bgSecondary, border: `1px solid ${COLORS.borderLight}` }}>
              <p className="mb-1" style={{ color: COLORS.textPrimary }}>
                <i className="bi bi-calendar3 me-2" style={{ color: COLORS.accent }}></i>
                <strong>{formatearFecha(cita.fecha)}</strong>
              </p>
              <p className="mb-1" style={{ color: COLORS.textPrimary }}>
                <i className="bi bi-clock me-2" style={{ color: COLORS.accent }}></i>
                {cita.horaInicio} {cita.horaFin && `- ${cita.horaFin}`}
              </p>
              <p className="mb-1" style={{ color: COLORS.textPrimary }}>
                <i className="bi bi-person-badge me-2" style={{ color: COLORS.accent }}></i>
                {getVetNombre()}
              </p>
              {cita.motivo && (
                <p className="mb-0 small" style={{ color: COLORS.textSecondary }}>
                  <i className="bi bi-chat-left-text me-2"></i>
                  {cita.motivo}
                </p>
              )}
            </div>
          </div>
          <div className="modal-footer border-0">
            <button
              className="btn"
              style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.borderLight}` }}
              onClick={onClose}
              disabled={isLoading}
            >
              No, mantener cita
            </button>
            <button
              className="btn"
              style={{ backgroundColor: COLORS.danger, color: '#fff', border: 'none' }}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Cancelando...
                </>
              ) : (
                <>
                  <i className="bi bi-x-lg me-2"></i>
                  Sí, cancelar cita
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────────────────────
export default function UserCalendarCitas() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Parámetros de URL
  const vetIdParam = searchParams.get("vetId") || "";
  const token = searchParams.get("token") || "";
  const userId = searchParams.get("uid") || "";
  const objetoId = searchParams.get("objetoId") || "";
  const objetoName = searchParams.get("objetoName") || "";
  const lugarNombreParam = searchParams.get("lugarNombre") || "";

  // Estados del flujo
  const [mode, setMode] = useState(
    vetIdParam ? 'booking' : lugarNombreParam ? 'reminder' : 'list'
  ); // 'list' | 'booking' | 'reminder' | 'addVet'
  const [selectedVetId, setSelectedVetId] = useState(vetIdParam);
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [duration, setDuration] = useState(60);
  const [motivo, setMotivo] = useState("");
  const [lugarPrellenado, setLugarPrellenado] = useState(lugarNombreParam);

  // Estados de datos
  const [veterinarios, setVeterinarios] = useState([]);
  const [vetData, setVetData] = useState(null);
  const [disponibilidad, setDisponibilidad] = useState([]);
  const [citas, setCitas] = useState([]);
  const [misCitas, setMisCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCitas, setLoadingCitas] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  // Estados para cancelación
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [citaToCancel, setCitaToCancel] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);

  // Estados para citas propuestas por veterinarios (pendientes de confirmación)
  const [citasPropuestas, setCitasPropuestas] = useState([]);
  const [loadingPropuestas, setLoadingPropuestas] = useState(true);
  const [processingCita, setProcessingCita] = useState(null);

  // Estados para recordatorios personales
  const [recordatorios, setRecordatorios] = useState([]);
  const [isCreatingReminder, setIsCreatingReminder] = useState(false);

  // Estado para agregar veterinario
  const [isAddingVet, setIsAddingVet] = useState(false);
  const [vetLimites, setVetLimites] = useState(null);

  // ─────────────────────────────────────────────────────────────
  // Cargar veterinarios del usuario
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    async function cargarVeterinarios() {
      try {
        // Intentar con endpoint que incluye límites
        const resp = await obtenerVeterinariosDeUsuario(userId);
        if (!resp.error) {
          setVeterinarios(resp.veterinarios || []);
          if (resp.limites) setVetLimites(resp.limites);
        } else {
          // Fallback al endpoint viejo
          const vets = await obtenerMisVeterinarios(userId);
          setVeterinarios(vets);
        }
      } catch (error) {
        console.error("Error al cargar veterinarios:", error);
        try {
          const resp = await obtenerVeterinarios();
          if (!resp.error) setVeterinarios(resp.data || []);
        } catch (e) {
          console.error("Error en fallback:", e);
        }
      } finally {
        setLoading(false);
      }
    }
    if (userId) cargarVeterinarios();
  }, [userId]);

  // ─────────────────────────────────────────────────────────────
  // Cargar MIS citas (de todos los veterinarios)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    async function cargarMisCitas() {
      if (!userId || veterinarios.length === 0) {
        setLoadingCitas(false);
        return;
      }

      setLoadingCitas(true);
      try {
        const todasLasCitas = [];

        for (const vet of veterinarios) {
          try {
            const citasVet = await getCitasDeVet(vet.id);
            // Filtrar solo mis citas para esta mascota
            const misCitasVet = citasVet
              .filter(c =>
                c.ownerId === userId &&
                c.objetoId === objetoId &&
                (c.estado === 'pendiente' || c.estado === 'confirmada')
              )
              .map(c => ({ ...c, vetId: vet.id }));
            todasLasCitas.push(...misCitasVet);
          } catch (err) {
            console.error(`Error cargando citas del vet ${vet.id}:`, err);
          }
        }

        // Ordenar por fecha y hora
        todasLasCitas.sort((a, b) => {
          const fechaA = new Date(`${a.fecha}T${a.horaInicio}`);
          const fechaB = new Date(`${b.fecha}T${b.horaInicio}`);
          return fechaA - fechaB;
        });

        setMisCitas(todasLasCitas);
      } catch (error) {
        console.error("Error al cargar mis citas:", error);
      } finally {
        setLoadingCitas(false);
      }
    }

    cargarMisCitas();
  }, [userId, objetoId, veterinarios]);

  // ─────────────────────────────────────────────────────────────
  // Cargar citas propuestas por veterinarios (pendientes de confirmación)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    async function cargarCitasPropuestas() {
      if (!userId) {
        setLoadingPropuestas(false);
        return;
      }

      setLoadingPropuestas(true);
      try {
        const resp = await obtenerCitasPendientes(userId);
        if (!resp.error) {
          // Filtrar por mascota si hay objetoId
          const citas = resp.data || [];
          const citasFiltradas = objetoId
            ? citas.filter(c => c.objetoId === objetoId)
            : citas;
          setCitasPropuestas(citasFiltradas);
        }
      } catch (error) {
        console.error("Error al cargar citas propuestas:", error);
      } finally {
        setLoadingPropuestas(false);
      }
    }

    cargarCitasPropuestas();
  }, [userId, objetoId]);

  // ─────────────────────────────────────────────────────────────
  // Confirmar cita propuesta por veterinario
  // ─────────────────────────────────────────────────────────────
  const handleConfirmarPropuesta = async (cita) => {
    setProcessingCita(cita.id);
    try {
      const resp = await confirmarCitaPropuesta(userId, cita.id, cita.vetId);
      if (resp.error) {
        toast.error(resp.error);
      } else {
        toast.success("Cita confirmada exitosamente");
        // Mover la cita a la lista de "mis citas"
        setCitasPropuestas(prev => prev.filter(c => c.id !== cita.id));
        setMisCitas(prev => [...prev, { ...cita, estado: 'confirmada' }].sort((a, b) =>
          new Date(`${a.fecha}T${a.horaInicio}`) - new Date(`${b.fecha}T${b.horaInicio}`)
        ));
      }
    } catch (error) {
      console.error("Error al confirmar:", error);
      toast.error("Error al confirmar la cita");
    } finally {
      setProcessingCita(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Rechazar cita propuesta por veterinario
  // ─────────────────────────────────────────────────────────────
  const handleRechazarPropuesta = async (cita) => {
    setProcessingCita(cita.id);
    try {
      const resp = await rechazarCitaPropuesta(userId, cita.id, cita.vetId, "No disponible");
      if (resp.error) {
        toast.error(resp.error);
      } else {
        toast.success("Cita rechazada");
        setCitasPropuestas(prev => prev.filter(c => c.id !== cita.id));
      }
    } catch (error) {
      console.error("Error al rechazar:", error);
      toast.error("Error al rechazar la cita");
    } finally {
      setProcessingCita(null);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Cargar recordatorios personales
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    async function cargarRecordatorios() {
      if (!userId) return;
      try {
        const resp = await obtenerRecordatoriosCitas(userId);
        if (!resp.error) {
          const recs = resp.data || [];
          // Filtrar por mascota si hay objetoId, y solo pendientes/futuros
          const hoy = new Date().toISOString().slice(0, 10);
          const filtrados = recs.filter(r =>
            (!objetoId || r.objetoId === objetoId) &&
            r.fecha >= hoy
          );
          setRecordatorios(filtrados);
        }
      } catch (error) {
        console.error("Error cargando recordatorios:", error);
      }
    }
    cargarRecordatorios();
  }, [userId, objetoId]);

  // ─────────────────────────────────────────────────────────────
  // Crear recordatorio personal
  // ─────────────────────────────────────────────────────────────
  const handleCrearRecordatorio = async (data) => {
    setIsCreatingReminder(true);
    try {
      const resp = await crearRecordatorioCita(userId, {
        ...data,
        objetoId: objetoId,
        mascotaNombre: objetoName ? decodeURIComponent(objetoName) : null,
      });
      if (resp.error) {
        toast.error(resp.error);
      } else {
        toast.success("Recordatorio guardado");
        setRecordatorios(prev => [...prev, resp.data].sort((a, b) => a.fecha.localeCompare(b.fecha)));
        setMode('list');
      }
    } catch (error) {
      console.error("Error creando recordatorio:", error);
      toast.error("Error al guardar el recordatorio");
    } finally {
      setIsCreatingReminder(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Eliminar recordatorio personal
  // ─────────────────────────────────────────────────────────────
  const handleEliminarRecordatorio = async (recordatorioId) => {
    try {
      const resp = await eliminarRecordatorioCita(userId, recordatorioId);
      if (resp.error) {
        toast.error(resp.error);
      } else {
        toast.success("Recordatorio eliminado");
        setRecordatorios(prev => prev.filter(r => r.id !== recordatorioId));
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Agregar veterinario (invitar)
  // ─────────────────────────────────────────────────────────────
  const handleAgregarVet = async (data) => {
    setIsAddingVet(true);
    try {
      const resp = await invitarVeterinarioAUsuario(userId, data);
      if (resp.error) {
        toast.error(resp.error);
      } else {
        toast.success("Veterinario agregado. Se envió una invitación.");
        // Recargar lista de veterinarios con límites
        try {
          const resp = await obtenerVeterinariosDeUsuario(userId);
          if (!resp.error) {
            setVeterinarios(resp.veterinarios || []);
            if (resp.limites) setVetLimites(resp.limites);
          }
        } catch (e) { /* ignore refresh error */ }
        setMode('list');
      }
    } catch (error) {
      console.error("Error al agregar veterinario:", error);
      toast.error("Error al agregar veterinario");
    } finally {
      setIsAddingVet(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Cargar disponibilidad y citas del veterinario seleccionado
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      if (!selectedVetId) return;
      setLoading(true);
      try {
        const blocks = await getDisponibilidadVet(selectedVetId);
        setDisponibilidad(blocks.map(b => ({ ...b, start: new Date(b.start), end: new Date(b.end) })));

        const citasData = await getCitasDeVet(selectedVetId);
        setCitas(citasData);

        const vet = veterinarios.find(v => v.id === selectedVetId);
        if (vet) setVetData(vet);
      } catch (error) {
        console.error("Error al cargar datos:", error);
        toast.error("Error al cargar disponibilidad");
      } finally {
        setLoading(false);
      }
    }
    if (mode === 'booking' && selectedVetId) loadData();
  }, [selectedVetId, veterinarios, mode]);

  // ─────────────────────────────────────────────────────────────
  // Calcular fechas y slots disponibles
  // ─────────────────────────────────────────────────────────────
  const availableDates = useMemo(() => {
    const fechasUnicas = new Set();
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    disponibilidad.forEach(bloque => {
      const fecha = new Date(bloque.start);
      fecha.setHours(0, 0, 0, 0);
      if (fecha >= hoy) fechasUnicas.add(fecha.toDateString());
    });

    return Array.from(fechasUnicas).map(str => new Date(str));
  }, [disponibilidad]);

  const availableSlots = useMemo(() => {
    if (!selectedDate) return [];

    const fechaStr = selectedDate.toISOString().slice(0, 10);
    const ahora = new Date();

    const bloquesDelDia = disponibilidad.filter(bloque => {
      return new Date(bloque.start).toISOString().slice(0, 10) === fechaStr;
    });

    const slots = [];
    bloquesDelDia.forEach(bloque => {
      let current = new Date(bloque.start);
      const end = new Date(bloque.end);

      while (current < end) {
        const hora = current.toTimeString().slice(0, 5);
        if (selectedDate.toDateString() === ahora.toDateString()) {
          if (current > ahora) slots.push(hora);
        } else {
          slots.push(hora);
        }
        current = new Date(current.getTime() + 60 * 60 * 1000);
      }
    });

    const citasDelDia = citas.filter(c =>
      c.fecha === fechaStr && (c.estado === 'confirmada' || c.estado === 'pendiente')
    );

    const slotsLibres = slots.filter(slot => {
      const [h, m] = slot.split(':');
      const slotStart = new Date(selectedDate);
      slotStart.setHours(parseInt(h), parseInt(m), 0, 0);
      const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);

      return !citasDelDia.some(cita => {
        const citaStart = new Date(`${cita.fecha}T${cita.horaInicio}:00`);
        const citaEnd = cita.horaFin
          ? new Date(`${cita.fecha}T${cita.horaFin}:00`)
          : new Date(citaStart.getTime() + 60 * 60 * 1000);
        return slotStart < citaEnd && slotEnd > citaStart;
      });
    });

    return [...new Set(slotsLibres)].sort();
  }, [selectedDate, disponibilidad, citas]);

  const maxDuration = useMemo(() => {
    if (!selectedDate || !selectedTime) return 120;

    const fechaStr = selectedDate.toISOString().slice(0, 10);
    const [h, m] = selectedTime.split(':');
    const slotStart = new Date(selectedDate);
    slotStart.setHours(parseInt(h), parseInt(m), 0, 0);

    const bloque = disponibilidad.find(b => {
      const bStart = new Date(b.start);
      const bEnd = new Date(b.end);
      return bStart.toISOString().slice(0, 10) === fechaStr &&
             slotStart >= bStart && slotStart < bEnd;
    });

    if (!bloque) return 60;

    const bloqueEnd = new Date(bloque.end);
    const citasDelDia = citas.filter(c =>
      c.fecha === fechaStr && (c.estado === 'confirmada' || c.estado === 'pendiente')
    );

    let maxEnd = bloqueEnd;
    citasDelDia.forEach(cita => {
      const citaStart = new Date(`${cita.fecha}T${cita.horaInicio}:00`);
      if (citaStart > slotStart && citaStart < maxEnd) maxEnd = citaStart;
    });

    const mins = (maxEnd - slotStart) / (1000 * 60);
    if (mins >= 120) return 120;
    if (mins >= 90) return 90;
    if (mins >= 60) return 60;
    return 30;
  }, [selectedDate, selectedTime, disponibilidad, citas]);

  useEffect(() => {
    if (duration > maxDuration) setDuration(maxDuration);
  }, [maxDuration, duration]);

  // ─────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────
  const handleSelectVet = (vetId) => {
    setSelectedVetId(vetId);
    setMode('booking');
    setStep(1);
  };

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep(2);
  };

  const handleSelectTime = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleBack = () => {
    if (mode === 'reminder') {
      setMode('list');
      setLugarPrellenado('');
    } else if (mode === 'addVet') {
      setMode('list');
    } else if (mode === 'booking') {
      if (step === 1) {
        setMode('list');
        setSelectedVetId('');
        setSelectedDate(null);
        setSelectedTime(null);
        setMotivo('');
      } else {
        setStep(step - 1);
        if (step === 2) setSelectedDate(null);
        if (step === 3) setSelectedTime(null);
      }
    } else {
      navigate(`/userPanel?doc=${userId}&token=${token}`);
    }
  };

  const handleConfirm = async () => {
    if (!motivo.trim()) {
      toast.error("Debes indicar el motivo");
      return;
    }

    const [h, m] = selectedTime.split(':');
    const inicio = new Date(selectedDate);
    inicio.setHours(parseInt(h), parseInt(m), 0, 0);
    const fin = new Date(inicio.getTime() + duration * 60 * 1000);

    const citaData = {
      objetoId: objetoId,
      objetoName: decodeURIComponent(objetoName),
      tipoConsulta: motivo.trim(),
      fecha: selectedDate.toISOString().slice(0, 10),
      horaInicio: selectedTime,
      horaFin: `${fin.getHours().toString().padStart(2, '0')}:${fin.getMinutes().toString().padStart(2, '0')}`,
      ownerId: userId,
      motivo: motivo.trim(),
    };

    setIsCreating(true);
    try {
      await crearCitaDeVet(selectedVetId, citaData);
      toast.success("Cita creada exitosamente");

      // Recargar citas y volver a lista
      setMode('list');
      setSelectedVetId('');
      setStep(1);
      setSelectedDate(null);
      setSelectedTime(null);
      setMotivo('');

      // Recargar mis citas
      const todasLasCitas = [];
      for (const vet of veterinarios) {
        try {
          const citasVet = await getCitasDeVet(vet.id);
          const misCitasVet = citasVet
            .filter(c => c.ownerId === userId && c.objetoId === objetoId &&
                        (c.estado === 'pendiente' || c.estado === 'confirmada'))
            .map(c => ({ ...c, vetId: vet.id }));
          todasLasCitas.push(...misCitasVet);
        } catch (e) {}
      }
      todasLasCitas.sort((a, b) => new Date(`${a.fecha}T${a.horaInicio}`) - new Date(`${b.fecha}T${b.horaInicio}`));
      setMisCitas(todasLasCitas);
    } catch (error) {
      console.error("Error:", error);
      toast.error("No se pudo crear la cita");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelarCita = async () => {
    if (!citaToCancel) return;

    setIsCanceling(true);
    try {
      await cancelarCitaDeVet(citaToCancel.vetId, citaToCancel.id);
      toast.success("Cita cancelada exitosamente");
      setMisCitas(prev => prev.filter(c => c.id !== citaToCancel.id));
      setShowCancelModal(false);
      setCitaToCancel(null);
    } catch (error) {
      console.error("Error al cancelar:", error);
      toast.error("No se pudo cancelar la cita");
    } finally {
      setIsCanceling(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", backgroundColor: COLORS.bgMain }}>
      {/* Header */}
      <div className="py-3" style={{ backgroundColor: COLORS.bgSecondary, borderBottom: `1px solid ${COLORS.border}` }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0" style={{ color: COLORS.textPrimary }}>
                <i className="bi bi-calendar-plus me-2" style={{ color: COLORS.accent }}></i>
                {mode === 'list' ? 'Gestionar Citas' : mode === 'reminder' ? 'Nuevo Recordatorio' : mode === 'addVet' ? 'Agregar Veterinario' : 'Agendar Cita'}
              </h5>
              <small style={{ color: COLORS.textSecondary }}>
                {objetoName && `Para: ${decodeURIComponent(objetoName)}`}
              </small>
            </div>
            <button
              className="btn btn-sm"
              style={{ color: COLORS.textPrimary, border: `1px solid ${COLORS.border}`, backgroundColor: 'transparent' }}
              onClick={handleBack}
            >
              <i className="bi bi-arrow-left me-1"></i>
              {mode === 'list' ? 'Volver' : mode === 'addVet' || mode === 'reminder' ? 'Cancelar' : (step === 1 ? 'Cancelar' : 'Atrás')}
            </button>
          </div>
        </div>
      </div>

      <div className="container py-4">
        {/* MODO LISTA: Mis Citas + Selector de Vet */}
        {mode === 'list' && (() => {
          const isOnboarding = !loading && veterinarios.length === 0 && misCitas.length === 0 && recordatorios.length === 0 && citasPropuestas.length === 0;

          return isOnboarding ? (
            // ── Onboarding: primera vez, sin datos ──
            <VetSelector
              veterinarios={veterinarios}
              onSelect={handleSelectVet}
              onBuscarVet={() => navigate('/localizador/resultados/veterinaria')}
              onCrearRecordatorio={() => setMode('reminder')}
              onAgregarVet={() => setMode('addVet')}
              isOnboarding={true}
            />
          ) : (
            // ── Returning user: citas, recordatorios, acciones ──
            <>
              {/* Citas propuestas por veterinarios */}
              <CitasPropuestasSection
                citas={citasPropuestas}
                loading={loadingPropuestas}
                processingCita={processingCita}
                onConfirmar={handleConfirmarPropuesta}
                onRechazar={handleRechazarPropuesta}
              />

              {/* Citas conectadas con veterinarios */}
              {(misCitas.length > 0 || loadingCitas) && (
                <MisCitasSection
                  citas={misCitas}
                  veterinarios={veterinarios}
                  loading={loadingCitas}
                  onCancelar={(cita) => {
                    setCitaToCancel(cita);
                    setShowCancelModal(true);
                  }}
                />
              )}

              {/* Recordatorios personales */}
              {recordatorios.length > 0 && (
                <div className="card mb-4" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
                  <div className="card-header" style={{ backgroundColor: COLORS.bgSecondary, borderBottom: `1px solid ${COLORS.border}` }}>
                    <h5 className="mb-0" style={{ color: COLORS.textPrimary }}>
                      <i className="bi bi-journal-text me-2" style={{ color: COLORS.warning }}></i>
                      Mis Recordatorios
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    {recordatorios.map(rec => {
                      const fechaObj = new Date(rec.fecha + 'T00:00:00');
                      const fechaTexto = fechaObj.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
                      const [rh, rm] = (rec.hora || '00:00').split(':');
                      const rHour = parseInt(rh);
                      const rAmpm = rHour >= 12 ? 'PM' : 'AM';
                      const rHour12 = rHour % 12 || 12;
                      const horaTexto = `${rHour12}:${rm} ${rAmpm}`;

                      return (
                        <div key={rec.id} className="d-flex align-items-center p-3" style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
                          <div
                            className="rounded d-flex align-items-center justify-content-center me-3"
                            style={{ width: 44, height: 44, backgroundColor: 'rgba(245, 158, 11, 0.1)', flexShrink: 0 }}
                          >
                            <i className="bi bi-journal-text" style={{ fontSize: '1.2rem', color: COLORS.warning }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div style={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                              <span className="text-capitalize">{fechaTexto}</span> · {horaTexto}
                            </div>
                            {rec.lugar && <small className="d-block" style={{ color: COLORS.textSecondary }}><i className="bi bi-geo-alt me-1"></i>{rec.lugar}</small>}
                            {rec.motivo && <small className="d-block" style={{ color: COLORS.textMuted }}>{rec.motivo}</small>}
                          </div>
                          <button
                            className="btn btn-sm"
                            style={{ color: COLORS.danger, border: 'none', backgroundColor: 'transparent' }}
                            onClick={() => handleEliminarRecordatorio(rec.id)}
                            title="Eliminar recordatorio"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Compact action section */}
              <div className="card" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
                <div className="card-header" style={{ backgroundColor: COLORS.bgSecondary, borderBottom: `1px solid ${COLORS.border}` }}>
                  <h5 className="mb-0" style={{ color: COLORS.textPrimary }}>
                    <i className="bi bi-plus-circle me-2" style={{ color: COLORS.success }}></i>
                    Agendar Nueva Cita
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" style={{ color: COLORS.accent }}></div>
                    </div>
                  ) : (
                    <VetSelector
                      veterinarios={veterinarios}
                      onSelect={handleSelectVet}
                      onBuscarVet={() => navigate('/localizador/resultados/veterinaria')}
                      onCrearRecordatorio={() => setMode('reminder')}
                      onAgregarVet={() => setMode('addVet')}
                      isOnboarding={false}
                    />
                  )}
                </div>
              </div>
            </>
          );
        })()}

        {/* MODO AGREGAR VET: Formulario de registro */}
        {mode === 'addVet' && (
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <AgregarVeterinarioModal
                onSave={handleAgregarVet}
                onCancel={() => setMode('list')}
                isLoading={isAddingVet}
                limites={vetLimites}
              />
            </div>
          </div>
        )}

        {/* MODO RECORDATORIO: Formulario simple */}
        {mode === 'reminder' && (
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <RecordatorioForm
                objetoName={objetoName}
                onSubmit={handleCrearRecordatorio}
                onCancel={() => { setMode('list'); setLugarPrellenado(''); }}
                isLoading={isCreatingReminder}
                lugarPrellenado={lugarPrellenado}
              />
            </div>
          </div>
        )}

        {/* MODO BOOKING: Flujo de 3 pasos */}
        {mode === 'booking' && (
          <>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" style={{ color: COLORS.accent }}></div>
                <p className="mt-3" style={{ color: COLORS.textSecondary }}>Cargando disponibilidad...</p>
              </div>
            ) : (
              <>
                <ProgressIndicator step={step} />

                {step === 1 && (
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                      <h5 className="text-center mb-4" style={{ color: COLORS.textPrimary }}>
                        <i className="bi bi-calendar3 me-2" style={{ color: COLORS.accent }}></i>
                        Selecciona una fecha
                      </h5>
                      {availableDates.length === 0 ? (
                        <div className="card" style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}>
                          <div className="card-body text-center py-5">
                            <i className="bi bi-calendar-x" style={{ fontSize: '3rem', color: COLORS.textMuted }}></i>
                            <h5 className="mt-3" style={{ color: COLORS.textPrimary }}>Sin disponibilidad</h5>
                            <p style={{ color: COLORS.textSecondary }}>No hay horarios disponibles.</p>
                          </div>
                        </div>
                      ) : (
                        <SimpleCalendar
                          availableDates={availableDates}
                          selectedDate={selectedDate}
                          onSelectDate={handleSelectDate}
                        />
                      )}
                    </div>
                  </div>
                )}

                {step === 2 && selectedDate && (
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                      <h5 className="text-center mb-4" style={{ color: COLORS.textPrimary }}>
                        <i className="bi bi-clock me-2" style={{ color: COLORS.accent }}></i>
                        Selecciona un horario
                      </h5>
                      <TimeSlotSelector
                        availableSlots={availableSlots}
                        selectedTime={selectedTime}
                        onSelectTime={handleSelectTime}
                        selectedDate={selectedDate}
                      />
                    </div>
                  </div>
                )}

                {step === 3 && selectedDate && selectedTime && (
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                      <AppointmentConfirmation
                        selectedDate={selectedDate}
                        selectedTime={selectedTime}
                        objetoName={objetoName}
                        vetName={vetData?.nombre}
                        duration={duration}
                        setDuration={setDuration}
                        motivo={motivo}
                        setMotivo={setMotivo}
                        onConfirm={handleConfirm}
                        isLoading={isCreating}
                        maxDuration={maxDuration}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Modal de Cancelación */}
      {showCancelModal && citaToCancel && (
        <CancelModal
          cita={citaToCancel}
          veterinarios={veterinarios}
          onConfirm={handleCancelarCita}
          onClose={() => {
            setShowCancelModal(false);
            setCitaToCancel(null);
          }}
          isLoading={isCanceling}
        />
      )}

    </div>
  );
}
